import { useState } from "react";
import type { WorkAgent } from "@ondo/shared";
import { mandateDaysRemaining } from "@ondo/shared";

import { CounterpartyCheck } from "../counterparty/CounterpartyCheck.tsx";
import { initialsOf } from "../lib/names.ts";
import { Tag } from "../lib/Tag.tsx";
import { WORK_AGENT_COPY } from "./work.constants.ts";
import type { AgentPanel, ScopeDraft } from "./work.model.ts";
import { describeAgentScope, scopeChanged, scopeDraftOf } from "./work.model.ts";

/**
 * Who represents this work.
 *
 * Sits in the left column beside the catalogue facts, where the prototype puts
 * "Current Owner" — the same eyebrow label, initials tile and name-over-meta
 * rows, because they answer the same shape of question about a work.
 *
 * A LIST AND NOT ONE NAME. A work may carry several mandates and an agent many
 * works, so co-representation and a separate consignment agent are both
 * expressible. The empty state says the record has no agent rather than showing
 * an empty list.
 *
 * Renders nothing at all when the caller may not assign and holds no mandate —
 * see `toAgentPanel`, which decides that from the server's capability rather
 * than from anything here.
 */
export function WorkAgentsPanel({
  panel,
  busy,
  onAssign,
  onEnd,
  onRenew,
  onSetScope,
}: {
  panel: AgentPanel;
  busy: boolean;
  onAssign: () => void;
  onEnd: (mandateId: string) => void;
  onRenew: (mandateId: string, expiresOn: string) => void;
  onSetScope: (mandateId: string, draft: ScopeDraft) => void;
}) {
  /**
   * The date typed against each mandate, before it is sent.
   *
   * LOCAL TO THE PANEL AND KEYED BY MANDATE, because it is a draft rather than
   * a fact: nothing outside this component needs to know what somebody has
   * half-typed into a date box, and two agents on one work must not share one
   * field. It is deliberately NOT lifted into `useWorkAgents` beside the appoint
   * draft — that one survives the dialog being closed and reopened, which is a
   * requirement; this one has no equivalent.
   */
  const [extending, setExtending] = useState<Record<string, string>>({});

  /**
   * The scope being reviewed against each mandate, before it is saved.
   *
   * LOCAL AND KEYED BY MANDATE, for `extending`'s reasons. What differs is that
   * an absent entry is not an empty draft: `scopeDraftOf` seeds it from the
   * APPOINTMENT, so the controls open on what is recorded rather than on
   * nothing. That is load-bearing rather than tidy — `POST /scope` states the
   * whole scope, so controls that opened blank and were saved would silently
   * remove an organisation nobody meant to remove.
   */
  const [scoping, setScoping] = useState<Record<string, ScopeDraft>>({});

  if (panel.kind === "hidden") return null;

  return (
    <section className="work__agents">
      <header className="work__agents-head">
        <h2 className="work__agents-title">{WORK_AGENT_COPY.title}</h2>
        {panel.kind === "listed" && panel.canAssign && (
          <button
            type="button"
            className="work__action"
            disabled={busy}
            onClick={onAssign}
          >
            {WORK_AGENT_COPY.assign}
          </button>
        )}
      </header>

      {panel.kind === "loading" ? (
        <p className="work__agents-empty" role="status">
          {panel.message}
        </p>
      ) : panel.agents.length === 0 ? (
        <p className="work__agents-empty">{WORK_AGENT_COPY.empty}</p>
      ) : (
        <ul className="work__agents-list">
          {panel.agents.map((agent) => (
            <li key={agent.mandateId} className="work__agent">
              <span className="work__agent-mark" aria-hidden="true">
                {initialsOf(agent.displayName, "·")}
              </span>
              <span className="work__agent-body">
                <span className="work__agent-name">
                  {agent.displayName}
                  {/* OND-81: who this is, from the id already on the row. */}
                  <CounterpartyCheck ondoId={agent.ondoId} />
                </span>
                <span className="work__agent-meta">
                  {agent.ondoId}
                  {agent.note ? ` · ${agent.note}` : ""}
                  {/* WHO BROUGHT THEM IN, when it was not the owner or the
                      gallery. An owner reading their own work can otherwise see
                      two agents and no way to tell which of them they chose. */}
                  {agent.delegatedBy
                    ? ` · ${WORK_AGENT_COPY.delegatedBy(agent.delegatedBy)}`
                    : ""}
                </span>
                {/* ── THE DAYS-REMAINING LINE ──
                    The thing 20260901090000 refused, and the refusal was about
                    its TRUTH rather than its wording: "inventing '121 days' from
                    created_at would put a deadline on screen that nothing
                    enforces." This counts to `expiresAt`, which
                    `ondo.live_mandate_ids()` reads in the statement that answers
                    every permission question.

                    A SEPARATE LINE RATHER THAN MORE OF `work__agent-meta`,
                    because it is the one fact on the row that changes by itself
                    and the one a reader is scanning for. */}
                <span className="work__agent-term">{describeTerm(agent)}</span>
                {/* ── WHO THE APPOINTMENT REACHES, OND-83 ──
                    ALWAYS RENDERED, including the narrow answer. "This agent
                    only" is a fact about the record that a collector is
                    entitled to read back, and the absence of a firm is not
                    allowed to be how they learn their colleagues are excluded
                    — the ticket's own example is somebody reading it exactly
                    that way round. See `WORK_AGENT_COPY.agentOnly`. */}
                <span className="work__agent-scope">
                  {describeAgentScope(agent)}
                </span>
              </span>
              {agent.verified && (
                <Tag tone="verified">{WORK_AGENT_COPY.verified}</Tag>
              )}
              {/* The owner's one permission, shown where it was granted. */}
              {agent.mayDelegate && (
                <Tag tone="neutral">{WORK_AGENT_COPY.mayDelegateTag}</Tag>
              )}
              {/* OND-82's, shown the same way and for the same reason: a power
                  over somebody's picture should be legible on the row that
                  granted it rather than only inside the form that did. */}
              {agent.scope.mayConsign && (
                <Tag tone="neutral">{WORK_AGENT_COPY.mayConsignTag}</Tag>
              )}
              {/* ── EXTEND ──
                  `canRenew` is computed by `ondo.work_agents()` from the same
                  four branches `ondo.renew_work_agent_mandate()` admits, plus
                  the one condition renewal adds — an open-ended appointment has
                  no term to renew — so this cannot offer a control the database
                  would refuse.

                  EITHER SIDE ALONE, which is why there is no proposal state and
                  nothing to wait for: OND-76's confirmed follow-up removed the
                  handshake. The title says so, because a collector who thought
                  they needed the agent's agreement would wait for it. */}
              {agent.canRenew && (
                <span className="work__agent-renew">
                  <label className="work__agent-renew-label">
                    <span className="ondo-visually-hidden">
                      {WORK_AGENT_COPY.extendLabel}
                    </span>
                    <input
                      type="date"
                      className="work__agent-renew-date"
                      value={extending[agent.mandateId] ?? ""}
                      disabled={busy}
                      onChange={(event) =>
                        setExtending((current) => ({
                          ...current,
                          [agent.mandateId]: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="work__agent-extend"
                    // Dead until a date has been chosen. The write would be
                    // refused without one, and an enabled button that cannot
                    // work is worse than a disabled one that says why.
                    disabled={busy || !extending[agent.mandateId]}
                    title={WORK_AGENT_COPY.extendHint}
                    onClick={() =>
                      onRenew(agent.mandateId, extending[agent.mandateId] ?? "")
                    }
                  >
                    {busy ? WORK_AGENT_COPY.extending : WORK_AGENT_COPY.extend}
                  </button>
                </span>
              )}
              {/* ── REVIEW AND UPDATE THE RECORDED SCOPE ──
                  OND-83: "Let authorised parties review and update the recorded
                  scope through the appointment permission controls. Record
                  changes; do not introduce an automatic transfer."

                  `canSetScope` is computed by `ondo.work_agents()` from the
                  same three branches `ondo.set_work_agent_mandate_scope()`
                  admits, so this cannot offer a control the database would
                  refuse — and it is NARROWER THAN `canEnd` by exactly one
                  branch. An agent reading their own appointment sees End and
                  does not see this: 20261003099000 decision 10, the appointed
                  side does not widen its own scope.

                  SAVE IS DEAD UNTIL SOMETHING CHANGES, because the database
                  refuses a change that changes nothing ("that is the scope this
                  appointment already has") and spending a round trip to be told
                  what the screen can see is the line `toAssignAgentView` draws
                  between the two kinds of problem. */}
              {agent.canSetScope && (
                <span className="work__agent-scope-edit">
                  <label className="work__agent-scope-org">
                    <span className="ondo-visually-hidden">
                      {WORK_AGENT_COPY.organisationLabel}
                    </span>
                    <input
                      className="work__agent-scope-input"
                      value={
                        (scoping[agent.mandateId] ?? scopeDraftOf(agent))
                          .organisationOndoId
                      }
                      placeholder={WORK_AGENT_COPY.idPlaceholder}
                      autoComplete="off"
                      spellCheck={false}
                      disabled={busy}
                      onChange={(event) =>
                        setScoping((current) => ({
                          ...current,
                          [agent.mandateId]: {
                            ...(current[agent.mandateId] ?? scopeDraftOf(agent)),
                            organisationOndoId: event.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="work__agent-scope-consign">
                    <input
                      type="checkbox"
                      checked={
                        (scoping[agent.mandateId] ?? scopeDraftOf(agent)).mayConsign
                      }
                      disabled={busy}
                      onChange={(event) =>
                        setScoping((current) => ({
                          ...current,
                          [agent.mandateId]: {
                            ...(current[agent.mandateId] ?? scopeDraftOf(agent)),
                            mayConsign: event.target.checked,
                          },
                        }))
                      }
                    />
                    <span>{WORK_AGENT_COPY.consignLabel}</span>
                  </label>
                  {/* OND-79's permission, in the SAME form as OND-82's and not
                      a control of its own, because the scope is one statement:
                      `POST /scope` replaces both permissions and the
                      organisation together, so a switch that sent one without
                      the other would revoke whichever it had not read.

                      THE SUB-LABEL SAYS WHAT IT CANNOT DO. An owner granting
                      this is not handing over their disclosure choices — an
                      agent's rules sit in a slot of their own and are unioned
                      onto the owner's, so they can only ever withhold MORE, and
                      the owner may remove any of them. A reader who thought
                      otherwise would never grant it. */}
                  <label className="work__agent-scope-disclosure">
                    <input
                      type="checkbox"
                      checked={
                        (scoping[agent.mandateId] ?? scopeDraftOf(agent))
                          .mayManageDisclosure
                      }
                      disabled={busy}
                      onChange={(event) =>
                        setScoping((current) => ({
                          ...current,
                          [agent.mandateId]: {
                            ...(current[agent.mandateId] ?? scopeDraftOf(agent)),
                            mayManageDisclosure: event.target.checked,
                          },
                        }))
                      }
                    />
                    <span>
                      {WORK_AGENT_COPY.discloseLabel}
                      <span className="work__agent-scope-note">
                        {WORK_AGENT_COPY.discloseNote}
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="work__agent-scope-save"
                    disabled={
                      busy ||
                      !scopeChanged(
                        agent,
                        scoping[agent.mandateId] ?? scopeDraftOf(agent),
                      )
                    }
                    title={WORK_AGENT_COPY.scopeHint}
                    onClick={() =>
                      onSetScope(
                        agent.mandateId,
                        scoping[agent.mandateId] ?? scopeDraftOf(agent),
                      )
                    }
                  >
                    {busy ? WORK_AGENT_COPY.scopeSaving : WORK_AGENT_COPY.scopeSave}
                  </button>
                </span>
              )}
              {/* `canEnd` is computed by `ondo.work_agents()` from the same sets
                  `ondo.end_work_agent_mandate()` checks, so this cannot offer a
                  control the database would refuse. */}
              {agent.canEnd && (
                <button
                  type="button"
                  className="work__agent-end"
                  disabled={busy}
                  title={WORK_AGENT_COPY.endWarning}
                  onClick={() => onEnd(agent.mandateId)}
                >
                  {WORK_AGENT_COPY.end}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * "121 days left", or the state instead of a number.
 *
 * DERIVED AND NEVER STORED, which is `covenantState`'s rule for this schema and
 * the database's own: `ondo.live_mandate_ids()` evaluates `expires_at > now()`
 * in the statement that asks, so a stored "expired" flag would be a second
 * answer to a question Postgres already answers correctly.
 *
 * AND THIS PANEL IS THE ONE PLACE "Term ended" CAN APPEAR. The owner's side
 * keeps seeing a lapsed appointment, because renewing one is an act that exists;
 * the AGENT's own list drops it, because an agent whose term ran out is a former
 * agent. 20261003096000 decisions 14 and 15 are the two halves of that.
 */
function describeTerm(agent: WorkAgent): string {
  const days = mandateDaysRemaining({ expiresAt: agent.term.expiresAt });

  if (days === null) return WORK_AGENT_COPY.openEnded;
  if (days < 0) return WORK_AGENT_COPY.expired;
  if (days === 0) return WORK_AGENT_COPY.endsToday;

  return WORK_AGENT_COPY.remaining(days);
}
